import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { InsurerMark } from "@/data/insurerBrands";
import { Phone, ArrowRight, ChevronDown, CheckCircle2, Shield, Sparkles, FileText } from "lucide-react";

export default function InsurerLanding() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [openFaq, setOpenFaq] = useState(0);
  const [error, setError] = useState("");

  useEffect(() => {
    setData(null);
    setError("");
    api.get(`/insurers/${slug}`).then((r) => {
      setData(r.data);
      // Basic SEO: set document title
      document.title = `${r.data.name} Motor Insurance Claim — myinsuranceclaim.in`;
      const desc = document.querySelector('meta[name="description"]');
      const content = `File and track ${r.data.name} motor insurance claims 5× faster with AI + a dedicated claim manager. Independent, cashless, 24×7.`;
      if (desc) desc.setAttribute("content", content);
      else {
        const m = document.createElement("meta");
        m.name = "description"; m.content = content;
        document.head.appendChild(m);
      }
    }).catch((e) => setError(e.response?.status === 404 ? "Insurer not found" : "Failed to load"));
  }, [slug]);

  if (error) {
    return (
      <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center text-center px-4" data-testid="insurer-not-found">
        <div>
          <FileText className="w-12 h-12 text-slate-300 mx-auto mb-2" />
          <p className="font-semibold text-slate-800 dark:text-slate-200">{error}</p>
          <Link to="/" className="text-sm text-[#003DA5] underline mt-2 inline-block">Back home</Link>
        </div>
      </div>
    );
  }
  if (!data) return <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center text-slate-500">Loading…</div>;

  return (
    <div data-testid="insurer-landing-page">
      {/* JSON-LD for FAQPage SEO */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": data.faqs.map(f => ({
          "@type": "Question",
          "name": f.q,
          "acceptedAnswer": { "@type": "Answer", "text": f.a }
        })),
      })}} />

      {/* HERO */}
      <section className="relative overflow-hidden mesh-hero py-20">
        <div className="aurora animate-aurora" style={{ width: 500, height: 500, top: -160, right: -100, background: "radial-gradient(circle, #003DA5 0%, transparent 60%)" }} />
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-8">
            <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full glass text-xs font-semibold text-slate-700 dark:text-slate-200 mb-5">
                <Shield className="w-3.5 h-3.5 text-[#003DA5]" /> Independent · Trusted by {data.name} policyholders
              </div>
              <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-black tracking-tighter leading-[1.05] text-slate-900 dark:text-white">
                {data.hero_headline}
              </h1>
              <p className="mt-5 text-slate-600 dark:text-slate-300 max-w-2xl">
                {data.hero_subline}
              </p>
              <div className="mt-6 flex flex-wrap gap-3">
                <Link to={`/file-claim?insurer=${data.slug}`} className="btn-signature rounded-xl px-6 py-3 font-semibold inline-flex items-center gap-2" data-testid="insurer-file-claim">
                  File a {data.name} claim <ArrowRight className="w-4 h-4" />
                </Link>
                <a href={`tel:${data.toll_free.replace(/\s/g, "")}`} className="btn-ghost-outline rounded-xl px-6 py-3 font-semibold inline-flex items-center gap-2" data-testid="insurer-call">
                  <Phone className="w-4 h-4" /> {data.toll_free}
                </a>
              </div>
              <div className="mt-6 flex flex-wrap gap-x-5 gap-y-2 text-xs text-slate-600 dark:text-slate-400">
                {data.trust_badges.map((b) => (
                  <span key={b} className="flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" /> {b}</span>
                ))}
              </div>
            </motion.div>
          </div>
          <div className="lg:col-span-4 flex justify-center">
            <div className="relative">
              <div className="glass-strong rounded-3xl p-8 shadow-2xl">
                <InsurerMark slug={data.slug} size={130} />
                <div className="mt-4 text-center">
                  <div className="font-heading font-black text-xl text-slate-900 dark:text-white">{data.name}</div>
                  <div className="text-xs text-slate-500 dark:text-slate-400 mt-1">Motor Insurance Partner</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* HOW WE HELP */}
      <section className="py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-10 max-w-2xl">
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#003DA5] dark:text-blue-400 mb-2">How we help</div>
            <h2 className="font-heading text-3xl font-extrabold tracking-tight text-slate-900 dark:text-white">
              Filing a {data.name} claim, the smart way.
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {data.steps.map((s, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.06 }}
                className="card-premium p-5"
                data-testid={`insurer-step-${i}`}
              >
                <div className="text-[10px] font-mono font-bold text-slate-400 mb-2 tracking-widest">STEP {String(i + 1).padStart(2, "0")}</div>
                <div className="font-heading font-bold text-lg text-slate-900 dark:text-white mb-1">{s.title}</div>
                <p className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed">{s.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* DOCS + FAQ */}
      <section className="py-20 mesh-subtle">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-12">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#FF7A00] mb-2">Documents needed</div>
            <h2 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-900 dark:text-white mb-5">
              Everything you&apos;ll need for your {data.name} claim.
            </h2>
            <ul className="space-y-2" data-testid="insurer-docs">
              {data.docs_required.map((d) => (
                <li key={d} className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-300">
                  <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0" /> {d}
                </li>
              ))}
            </ul>
            <div className="mt-6 rounded-xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-4">
              <div className="flex items-center gap-2 mb-1"><Sparkles className="w-4 h-4 text-[#003DA5]" /><span className="font-heading font-bold text-slate-900 dark:text-white">Pro tip</span></div>
              <p className="text-sm text-slate-600 dark:text-slate-400">Upload docs to your vault once — we auto-fill every field with AI OCR the next time you file.</p>
            </div>
          </div>

          <div>
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#003DA5] dark:text-blue-400 mb-2">FAQ</div>
            <h2 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-900 dark:text-white mb-5">
              {data.name} claim questions, answered.
            </h2>
            <div className="space-y-2" data-testid="insurer-faqs">
              {data.faqs.map((f, i) => (
                <div key={i} className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl overflow-hidden">
                  <button
                    onClick={() => setOpenFaq(openFaq === i ? -1 : i)}
                    className="w-full flex items-center justify-between text-left p-4 font-semibold text-slate-900 dark:text-white hover:bg-slate-50 dark:hover:bg-slate-800"
                    data-testid={`insurer-faq-toggle-${i}`}
                  >
                    <span>{f.q}</span>
                    <ChevronDown className={`w-4 h-4 shrink-0 transition-transform ${openFaq === i ? "rotate-180" : ""}`} />
                  </button>
                  {openFaq === i && (
                    <div className="px-4 pb-4 text-sm text-slate-600 dark:text-slate-400 leading-relaxed" data-testid={`insurer-faq-body-${i}`}>
                      {f.a}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="rounded-3xl bg-gradient-to-br from-[#003DA5] via-[#0055D4] to-[#003DA5] text-white p-10 text-center relative overflow-hidden">
            <div className="absolute -top-24 -right-24 w-72 h-72 rounded-full bg-[#FF7A00]/40 blur-3xl" />
            <div className="relative">
              <h2 className="font-heading text-3xl sm:text-4xl font-black tracking-tight">Ready to file your {data.name} claim?</h2>
              <p className="mt-3 opacity-90">Takes 3 minutes online. A claim manager takes over from step 2.</p>
              <div className="mt-6 flex justify-center flex-wrap gap-3">
                <Link to={`/file-claim?insurer=${data.slug}`} className="bg-white text-[#003DA5] font-bold rounded-xl px-6 py-3 inline-flex items-center gap-2" data-testid="insurer-cta-file">
                  Start now <ArrowRight className="w-4 h-4" />
                </Link>
                <a href={`tel:${data.toll_free.replace(/\s/g, "")}`} className="bg-white/15 border border-white/25 hover:bg-white/25 font-bold rounded-xl px-6 py-3 inline-flex items-center gap-2">
                  <Phone className="w-4 h-4" /> Talk to us
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
